/**
 * @license
 * Copyright 2018 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const ze = /* @__PURE__ */ new Set(["children", "localName", "ref", "style", "className"]), re = /* @__PURE__ */ new WeakMap(), se = (t, e, n, r, s) => {
  const o = s == null ? void 0 : s[e];
  o === void 0 ? (t[e] = n, n == null && e in HTMLElement.prototype && t.removeAttribute(e)) : n !== r && ((i, a, c) => {
    let l = re.get(i);
    l === void 0 && re.set(i, l = /* @__PURE__ */ new Map());
    let d = l.get(a);
    c !== void 0 ? d === void 0 ? (l.set(a, d = { handleEvent: c }), i.addEventListener(a, d)) : d.handleEvent = c : d !== void 0 && (l.delete(a), i.removeEventListener(a, d));
  })(t, o, n);
}, Dt = ({ react: t, tagName: e, elementClass: n, events: r, displayName: s }) => {
  const o = new Set(Object.keys(r != null ? r : {})), i = t.forwardRef((a, c) => {
    const l = t.useRef(/* @__PURE__ */ new Map()), d = t.useRef(null), p = {}, f = {};
    for (const [u, m] of Object.entries(a)) ze.has(u) ? p[u === "className" ? "class" : u] = m : o.has(u) || u in n.prototype ? f[u] = m : p[u] = m;
    return t.useLayoutEffect(() => {
      if (d.current === null) return;
      const u = /* @__PURE__ */ new Map();
      for (const m in f) se(d.current, m, a[m], l.current.get(m), r), l.current.delete(m), u.set(m, a[m]);
      for (const [m, Ve] of l.current) se(d.current, m, void 0, Ve, r);
      l.current = u;
    }), t.useLayoutEffect(() => {
      var u;
      (u = d.current) == null || u.removeAttribute("defer-hydration");
    }, []), p.suppressHydrationWarning = !0, t.createElement(e, { ...p, ref: t.useCallback((u) => {
      d.current = u, typeof c == "function" ? c(u) : c !== null && (c.current = u);
    }, [c]) });
  });
  return i.displayName = s != null ? s : n.name, i;
};
/**
 * @license
 * Copyright 2019 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const V = globalThis, X = V.ShadowRoot && (V.ShadyCSS === void 0 || V.ShadyCSS.nativeShadow) && "adoptedStyleSheets" in Document.prototype && "replace" in CSSStyleSheet.prototype, J = Symbol(), ie = /* @__PURE__ */ new WeakMap();
let Ce = class {
  constructor(e, n, r) {
    if (this._$cssResult$ = !0, r !== J) throw Error("CSSResult is not constructable. Use `unsafeCSS` or `css` instead.");
    this.cssText = e, this.t = n;
  }
  get styleSheet() {
    let e = this.o;
    const n = this.t;
    if (X && e === void 0) {
      const r = n !== void 0 && n.length === 1;
      r && (e = ie.get(n)), e === void 0 && ((this.o = e = new CSSStyleSheet()).replaceSync(this.cssText), r && ie.set(n, e));
    }
    return e;
  }
  toString() {
    return this.cssText;
  }
};
const Ke = (t) => new Ce(typeof t == "string" ? t : t + "", void 0, J), T = (t, ...e) => {
  const n = t.length === 1 ? t[0] : e.reduce((r, s, o) => r + ((i) => {
    if (i._$cssResult$ === !0) return i.cssText;
    if (typeof i == "number") return i;
    throw Error("Value passed to 'css' function must be a 'css' function result: " + i + ". Use 'unsafeCSS' to pass non-literal values, but take care to ensure page security.");
  })(s) + t[o + 1], t[0]);
  return new Ce(n, t, J);
}, We = (t, e) => {
  if (X) t.adoptedStyleSheets = e.map((n) => n instanceof CSSStyleSheet ? n : n.styleSheet);
  else for (const n of e) {
    const r = document.createElement("style"), s = V.litNonce;
    s !== void 0 && r.setAttribute("nonce", s), r.textContent = n.cssText, t.appendChild(r);
  }
}, oe = X ? (t) => t : (t) => t instanceof CSSStyleSheet ? ((e) => {
  let n = "";
  for (const r of e.cssRules) n += r.cssText;
  return Ke(n);
})(t) : t;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const { is: Ge, defineProperty: qe, getOwnPropertyDescriptor: Fe, getOwnPropertyNames: Ye, getOwnPropertySymbols: Ze, getPrototypeOf: Xe } = Object, D = globalThis, ae = D.trustedTypes, Je = ae ? ae.emptyScript : "", ce = D.reactiveElementPolyfillSupport, M = (t, e) => t, K = { toAttribute(t, e) {
  switch (e) {
    case Boolean:
      t = t ? Je : null;
      break;
    case Object:
    case Array:
      t = t == null ? t : JSON.stringify(t);
  }
  return t;
}, fromAttribute(t, e) {
  let n = t;
  switch (e) {
    case Boolean:
      n = t !== null;
      break;
    case Number:
      n = t === null ? null : Number(t);
      break;
    case Object:
    case Array:
      try {
        n = JSON.parse(t);
      } catch {
        n = null;
      }
  }
  return n;
} }, Q = (t, e) => !Ge(t, e), le = { attribute: !0, type: String, converter: K, reflect: !1, hasChanged: Q };
Symbol.metadata != null || (Symbol.metadata = Symbol("metadata")), D.litPropertyMetadata != null || (D.litPropertyMetadata = /* @__PURE__ */ new WeakMap());
let j = class extends HTMLElement {
  static addInitializer(e) {
    var n;
    this._$Ei(), ((n = this.l) != null ? n : this.l = []).push(e);
  }
  static get observedAttributes() {
    return this.finalize(), this._$Eh && [...this._$Eh.keys()];
  }
  static createProperty(e, n = le) {
    if (n.state && (n.attribute = !1), this._$Ei(), this.elementProperties.set(e, n), !n.noAccessor) {
      const r = Symbol(), s = this.getPropertyDescriptor(e, r, n);
      s !== void 0 && qe(this.prototype, e, s);
    }
  }
  static getPropertyDescriptor(e, n, r) {
    var s;
    const { get: o, set: i } = (s = Fe(this.prototype, e)) != null ? s : { get() {
      return this[n];
    }, set(a) {
      this[n] = a;
    } };
    return { get() {
      return o == null ? void 0 : o.call(this);
    }, set(a) {
      const c = o == null ? void 0 : o.call(this);
      i.call(this, a), this.requestUpdate(e, c, r);
    }, configurable: !0, enumerable: !0 };
  }
  static getPropertyOptions(e) {
    var n;
    return (n = this.elementProperties.get(e)) != null ? n : le;
  }
  static _$Ei() {
    if (this.hasOwnProperty(M("elementProperties"))) return;
    const e = Xe(this);
    e.finalize(), e.l !== void 0 && (this.l = [...e.l]), this.elementProperties = new Map(e.elementProperties);
  }
  static finalize() {
    if (this.hasOwnProperty(M("finalized"))) return;
    if (this.finalized = !0, this._$Ei(), this.hasOwnProperty(M("properties"))) {
      const n = this.properties, r = [...Ye(n), ...Ze(n)];
      for (const s of r) this.createProperty(s, n[s]);
    }
    const e = this[Symbol.metadata];
    if (e !== null) {
      const n = litPropertyMetadata.get(e);
      if (n !== void 0) for (const [r, s] of n) this.elementProperties.set(r, s);
    }
    this._$Eh = /* @__PURE__ */ new Map();
    for (const [n, r] of this.elementProperties) {
      const s = this._$Eu(n, r);
      s !== void 0 && this._$Eh.set(s, n);
    }
    this.elementStyles = this.finalizeStyles(this.styles);
  }
  static finalizeStyles(e) {
    const n = [];
    if (Array.isArray(e)) {
      const r = new Set(e.flat(1 / 0).reverse());
      for (const s of r) n.unshift(oe(s));
    } else e !== void 0 && n.push(oe(e));
    return n;
  }
  static _$Eu(e, n) {
    const r = n.attribute;
    return r === !1 ? void 0 : typeof r == "string" ? r : typeof e == "string" ? e.toLowerCase() : void 0;
  }
  constructor() {
    super(), this._$Ep = void 0, this.isUpdatePending = !1, this.hasUpdated = !1, this._$Em = null, this._$Ev();
  }
  _$Ev() {
    var e;
    this._$ES = new Promise((n) => this.enableUpdating = n), this._$AL = /* @__PURE__ */ new Map(), this._$E_(), this.requestUpdate(), (e = this.constructor.l) == null || e.forEach((n) => n(this));
  }
  addController(e) {
    var n, r;
    ((n = this._$EO) != null ? n : this._$EO = /* @__PURE__ */ new Set()).add(e), this.renderRoot !== void 0 && this.isConnected && ((r = e.hostConnected) == null || r.call(e));
  }
  removeController(e) {
    var n;
    (n = this._$EO) == null || n.delete(e);
  }
  _$E_() {
    const e = /* @__PURE__ */ new Map(), n = this.constructor.elementProperties;
    for (const r of n.keys()) this.hasOwnProperty(r) && (e.set(r, this[r]), delete this[r]);
    e.size > 0 && (this._$Ep = e);
  }
  createRenderRoot() {
    var e;
    const n = (e = this.shadowRoot) != null ? e : this.attachShadow(this.constructor.shadowRootOptions);
    return We(n, this.constructor.elementStyles), n;
  }
  connectedCallback() {
    var e;
    this.renderRoot != null || (this.renderRoot = this.createRenderRoot()), this.enableUpdating(!0), (e = this._$EO) == null || e.forEach((n) => {
      var r;
      return (r = n.hostConnected) == null ? void 0 : r.call(n);
    });
  }
  enableUpdating(e) {
  }
  disconnectedCallback() {
    var e;
    (e = this._$EO) == null || e.forEach((n) => {
      var r;
      return (r = n.hostDisconnected) == null ? void 0 : r.call(n);
    });
  }
  attributeChangedCallback(e, n, r) {
    this._$AK(e, r);
  }
  _$EC(e, n) {
    var r;
    const s = this.constructor.elementProperties.get(e), o = this.constructor._$Eu(e, s);
    if (o !== void 0 && s.reflect === !0) {
      const i = (((r = s.converter) == null ? void 0 : r.toAttribute) !== void 0 ? s.converter : K).toAttribute(n, s.type);
      this._$Em = e, i == null ? this.removeAttribute(o) : this.setAttribute(o, i), this._$Em = null;
    }
  }
  _$AK(e, n) {
    var r;
    const s = this.constructor, o = s._$Eh.get(e);
    if (o !== void 0 && this._$Em !== o) {
      const i = s.getPropertyOptions(o), a = typeof i.converter == "function" ? { fromAttribute: i.converter } : ((r = i.converter) == null ? void 0 : r.fromAttribute) !== void 0 ? i.converter : K;
      this._$Em = o, this[o] = a.fromAttribute(n, i.type), this._$Em = null;
    }
  }
  requestUpdate(e, n, r) {
    var s;
    if (e !== void 0) {
      if (r != null || (r = this.constructor.getPropertyOptions(e)), !((s = r.hasChanged) != null ? s : Q)(this[e], n)) return;
      this.P(e, n, r);
    }
    this.isUpdatePending === !1 && (this._$ES = this._$ET());
  }
  P(e, n, r) {
    var s;
    this._$AL.has(e) || this._$AL.set(e, n), r.reflect === !0 && this._$Em !== e && ((s = this._$Ej) != null ? s : this._$Ej = /* @__PURE__ */ new Set()).add(e);
  }
  async _$ET() {
    this.isUpdatePending = !0;
    try {
      await this._$ES;
    } catch (n) {
      Promise.reject(n);
    }
    const e = this.scheduleUpdate();
    return e != null && await e, !this.isUpdatePending;
  }
  scheduleUpdate() {
    return this.performUpdate();
  }
  performUpdate() {
    var e;
    if (!this.isUpdatePending) return;
    if (!this.hasUpdated) {
      if (this.renderRoot != null || (this.renderRoot = this.createRenderRoot()), this._$Ep) {
        for (const [o, i] of this._$Ep) this[o] = i;
        this._$Ep = void 0;
      }
      const s = this.constructor.elementProperties;
      if (s.size > 0) for (const [o, i] of s) i.wrapped !== !0 || this._$AL.has(o) || this[o] === void 0 || this.P(o, this[o], i);
    }
    let n = !1;
    const r = this._$AL;
    try {
      n = this.shouldUpdate(r), n ? (this.willUpdate(r), (e = this._$EO) == null || e.forEach((s) => {
        var o;
        return (o = s.hostUpdate) == null ? void 0 : o.call(s);
      }), this.update(r)) : this._$EU();
    } catch (s) {
      throw n = !1, this._$EU(), s;
    }
    n && this._$AE(r);
  }
  willUpdate(e) {
  }
  _$AE(e) {
    var n;
    (n = this._$EO) == null || n.forEach((r) => {
      var s;
      return (s = r.hostUpdated) == null ? void 0 : s.call(r);
    }), this.hasUpdated || (this.hasUpdated = !0, this.firstUpdated(e)), this.updated(e);
  }
  _$EU() {
    this._$AL = /* @__PURE__ */ new Map(), this.isUpdatePending = !1;
  }
  get updateComplete() {
    return this.getUpdateComplete();
  }
  getUpdateComplete() {
    return this._$ES;
  }
  shouldUpdate(e) {
    return !0;
  }
  update(e) {
    this._$Ej && (this._$Ej = this._$Ej.forEach((n) => this._$EC(n, this[n]))), this._$EU();
  }
  updated(e) {
  }
  firstUpdated(e) {
  }
};
var de;
j.elementStyles = [], j.shadowRootOptions = { mode: "open" }, j[M("elementProperties")] = /* @__PURE__ */ new Map(), j[M("finalized")] = /* @__PURE__ */ new Map(), ce == null || ce({ ReactiveElement: j }), ((de = D.reactiveElementVersions) != null ? de : D.reactiveElementVersions = []).push("2.0.4");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const W = globalThis, G = W.trustedTypes, pe = G ? G.createPolicy("lit-html", { createHTML: (t) => t }) : void 0, ee = "$lit$", $ = `lit$${Math.random().toFixed(9).slice(2)}$`, te = "?" + $, Qe = `<${te}>`, S = document, I = () => S.createComment(""), H = (t) => t === null || typeof t != "object" && typeof t != "function", ne = Array.isArray, je = (t) => ne(t) || typeof (t == null ? void 0 : t[Symbol.iterator]) == "function", F = `[ 	
\f\r]`, R = /<(?:(!--|\/[^a-zA-Z])|(\/?[a-zA-Z][^>\s]*)|(\/?$))/g, ue = /-->/g, he = />/g, x = RegExp(`>|${F}(?:([^\\s"'>=/]+)(${F}*=${F}*(?:[^ 	
\f\r"'\`<>=]|("|')|))|$)`, "g"), me = /'/g, fe = /"/g, Oe = /^(?:script|style|textarea|title)$/i, et = (t) => (e, ...n) => ({ _$litType$: t, strings: e, values: n }), tt = et(1), N = Symbol.for("lit-noChange"), h = Symbol.for("lit-nothing"), be = /* @__PURE__ */ new WeakMap(), w = S.createTreeWalker(S, 129);
function Pe(t, e) {
  if (!ne(t) || !t.hasOwnProperty("raw")) throw Error("invalid template strings array");
  return pe !== void 0 ? pe.createHTML(e) : e;
}
const De = (t, e) => {
  const n = t.length - 1, r = [];
  let s, o = e === 2 ? "<svg>" : e === 3 ? "<math>" : "", i = R;
  for (let a = 0; a < n; a++) {
    const c = t[a];
    let l, d, p = -1, f = 0;
    for (; f < c.length && (i.lastIndex = f, d = i.exec(c), d !== null); ) f = i.lastIndex, i === R ? d[1] === "!--" ? i = ue : d[1] !== void 0 ? i = he : d[2] !== void 0 ? (Oe.test(d[2]) && (s = RegExp("</" + d[2], "g")), i = x) : d[3] !== void 0 && (i = x) : i === x ? d[0] === ">" ? (i = s != null ? s : R, p = -1) : d[1] === void 0 ? p = -2 : (p = i.lastIndex - d[2].length, l = d[1], i = d[3] === void 0 ? x : d[3] === '"' ? fe : me) : i === fe || i === me ? i = x : i === ue || i === he ? i = R : (i = x, s = void 0);
    const u = i === x && t[a + 1].startsWith("/>") ? " " : "";
    o += i === R ? c + Qe : p >= 0 ? (r.push(l), c.slice(0, p) + ee + c.slice(p) + $ + u) : c + $ + (p === -2 ? a : u);
  }
  return [Pe(t, o + (t[n] || "<?>") + (e === 2 ? "</svg>" : e === 3 ? "</math>" : "")), r];
};
let Z = class Ne {
  constructor({ strings: e, _$litType$: n }, r) {
    let s;
    this.parts = [];
    let o = 0, i = 0;
    const a = e.length - 1, c = this.parts, [l, d] = De(e, n);
    if (this.el = Ne.createElement(l, r), w.currentNode = this.el.content, n === 2 || n === 3) {
      const p = this.el.content.firstChild;
      p.replaceWith(...p.childNodes);
    }
    for (; (s = w.nextNode()) !== null && c.length < a; ) {
      if (s.nodeType === 1) {
        if (s.hasAttributes()) for (const p of s.getAttributeNames()) if (p.endsWith(ee)) {
          const f = d[i++], u = s.getAttribute(p).split($), m = /([.?@])?(.*)/.exec(f);
          c.push({ type: 1, index: o, name: m[2], strings: u, ctor: m[1] === "." ? Le : m[1] === "?" ? Ue : m[1] === "@" ? ke : B }), s.removeAttribute(p);
        } else p.startsWith($) && (c.push({ type: 6, index: o }), s.removeAttribute(p));
        if (Oe.test(s.tagName)) {
          const p = s.textContent.split($), f = p.length - 1;
          if (f > 0) {
            s.textContent = G ? G.emptyScript : "";
            for (let u = 0; u < f; u++) s.append(p[u], I()), w.nextNode(), c.push({ type: 2, index: ++o });
            s.append(p[f], I());
          }
        }
      } else if (s.nodeType === 8) if (s.data === te) c.push({ type: 2, index: o });
      else {
        let p = -1;
        for (; (p = s.data.indexOf($, p + 1)) !== -1; ) c.push({ type: 7, index: o }), p += $.length - 1;
      }
      o++;
    }
  }
  static createElement(e, n) {
    const r = S.createElement("template");
    return r.innerHTML = e, r;
  }
};
function C(t, e, n = t, r) {
  var s, o, i;
  if (e === N) return e;
  let a = r !== void 0 ? (s = n._$Co) == null ? void 0 : s[r] : n._$Cl;
  const c = H(e) ? void 0 : e._$litDirective$;
  return (a == null ? void 0 : a.constructor) !== c && ((o = a == null ? void 0 : a._$AO) == null || o.call(a, !1), c === void 0 ? a = void 0 : (a = new c(t), a._$AT(t, n, r)), r !== void 0 ? ((i = n._$Co) != null ? i : n._$Co = [])[r] = a : n._$Cl = a), a !== void 0 && (e = C(t, a._$AS(t, e.values), a, r)), e;
}
class Te {
  constructor(e, n) {
    this._$AV = [], this._$AN = void 0, this._$AD = e, this._$AM = n;
  }
  get parentNode() {
    return this._$AM.parentNode;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  u(e) {
    var n;
    const { el: { content: r }, parts: s } = this._$AD, o = ((n = e == null ? void 0 : e.creationScope) != null ? n : S).importNode(r, !0);
    w.currentNode = o;
    let i = w.nextNode(), a = 0, c = 0, l = s[0];
    for (; l !== void 0; ) {
      if (a === l.index) {
        let d;
        l.type === 2 ? d = new q(i, i.nextSibling, this, e) : l.type === 1 ? d = new l.ctor(i, l.name, l.strings, this, e) : l.type === 6 && (d = new Me(i, this, e)), this._$AV.push(d), l = s[++c];
      }
      a !== (l == null ? void 0 : l.index) && (i = w.nextNode(), a++);
    }
    return w.currentNode = S, o;
  }
  p(e) {
    let n = 0;
    for (const r of this._$AV) r !== void 0 && (r.strings !== void 0 ? (r._$AI(e, r, n), n += r.strings.length - 2) : r._$AI(e[n])), n++;
  }
}
let q = class Re {
  get _$AU() {
    var e, n;
    return (n = (e = this._$AM) == null ? void 0 : e._$AU) != null ? n : this._$Cv;
  }
  constructor(e, n, r, s) {
    var o;
    this.type = 2, this._$AH = h, this._$AN = void 0, this._$AA = e, this._$AB = n, this._$AM = r, this.options = s, this._$Cv = (o = s == null ? void 0 : s.isConnected) != null ? o : !0;
  }
  get parentNode() {
    let e = this._$AA.parentNode;
    const n = this._$AM;
    return n !== void 0 && (e == null ? void 0 : e.nodeType) === 11 && (e = n.parentNode), e;
  }
  get startNode() {
    return this._$AA;
  }
  get endNode() {
    return this._$AB;
  }
  _$AI(e, n = this) {
    e = C(this, e, n), H(e) ? e === h || e == null || e === "" ? (this._$AH !== h && this._$AR(), this._$AH = h) : e !== this._$AH && e !== N && this._(e) : e._$litType$ !== void 0 ? this.$(e) : e.nodeType !== void 0 ? this.T(e) : je(e) ? this.k(e) : this._(e);
  }
  O(e) {
    return this._$AA.parentNode.insertBefore(e, this._$AB);
  }
  T(e) {
    this._$AH !== e && (this._$AR(), this._$AH = this.O(e));
  }
  _(e) {
    this._$AH !== h && H(this._$AH) ? this._$AA.nextSibling.data = e : this.T(S.createTextNode(e)), this._$AH = e;
  }
  $(e) {
    var n;
    const { values: r, _$litType$: s } = e, o = typeof s == "number" ? this._$AC(e) : (s.el === void 0 && (s.el = Z.createElement(Pe(s.h, s.h[0]), this.options)), s);
    if (((n = this._$AH) == null ? void 0 : n._$AD) === o) this._$AH.p(r);
    else {
      const i = new Te(o, this), a = i.u(this.options);
      i.p(r), this.T(a), this._$AH = i;
    }
  }
  _$AC(e) {
    let n = be.get(e.strings);
    return n === void 0 && be.set(e.strings, n = new Z(e)), n;
  }
  k(e) {
    ne(this._$AH) || (this._$AH = [], this._$AR());
    const n = this._$AH;
    let r, s = 0;
    for (const o of e) s === n.length ? n.push(r = new Re(this.O(I()), this.O(I()), this, this.options)) : r = n[s], r._$AI(o), s++;
    s < n.length && (this._$AR(r && r._$AB.nextSibling, s), n.length = s);
  }
  _$AR(e = this._$AA.nextSibling, n) {
    var r;
    for ((r = this._$AP) == null ? void 0 : r.call(this, !1, !0, n); e && e !== this._$AB; ) {
      const s = e.nextSibling;
      e.remove(), e = s;
    }
  }
  setConnected(e) {
    var n;
    this._$AM === void 0 && (this._$Cv = e, (n = this._$AP) == null || n.call(this, e));
  }
}, B = class {
  get tagName() {
    return this.element.tagName;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  constructor(e, n, r, s, o) {
    this.type = 1, this._$AH = h, this._$AN = void 0, this.element = e, this.name = n, this._$AM = s, this.options = o, r.length > 2 || r[0] !== "" || r[1] !== "" ? (this._$AH = Array(r.length - 1).fill(new String()), this.strings = r) : this._$AH = h;
  }
  _$AI(e, n = this, r, s) {
    const o = this.strings;
    let i = !1;
    if (o === void 0) e = C(this, e, n, 0), i = !H(e) || e !== this._$AH && e !== N, i && (this._$AH = e);
    else {
      const a = e;
      let c, l;
      for (e = o[0], c = 0; c < o.length - 1; c++) l = C(this, a[r + c], n, c), l === N && (l = this._$AH[c]), i || (i = !H(l) || l !== this._$AH[c]), l === h ? e = h : e !== h && (e += (l != null ? l : "") + o[c + 1]), this._$AH[c] = l;
    }
    i && !s && this.j(e);
  }
  j(e) {
    e === h ? this.element.removeAttribute(this.name) : this.element.setAttribute(this.name, e != null ? e : "");
  }
};
class Le extends B {
  constructor() {
    super(...arguments), this.type = 3;
  }
  j(e) {
    this.element[this.name] = e === h ? void 0 : e;
  }
}
class Ue extends B {
  constructor() {
    super(...arguments), this.type = 4;
  }
  j(e) {
    this.element.toggleAttribute(this.name, !!e && e !== h);
  }
}
class ke extends B {
  constructor(e, n, r, s, o) {
    super(e, n, r, s, o), this.type = 5;
  }
  _$AI(e, n = this) {
    var r;
    if ((e = (r = C(this, e, n, 0)) != null ? r : h) === N) return;
    const s = this._$AH, o = e === h && s !== h || e.capture !== s.capture || e.once !== s.once || e.passive !== s.passive, i = e !== h && (s === h || o);
    o && this.element.removeEventListener(this.name, this, s), i && this.element.addEventListener(this.name, this, e), this._$AH = e;
  }
  handleEvent(e) {
    var n, r;
    typeof this._$AH == "function" ? this._$AH.call((r = (n = this.options) == null ? void 0 : n.host) != null ? r : this.element, e) : this._$AH.handleEvent(e);
  }
}
class Me {
  constructor(e, n, r) {
    this.element = e, this.type = 6, this._$AN = void 0, this._$AM = n, this.options = r;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AI(e) {
    C(this, e);
  }
}
const Lt = { M: ee, P: $, A: te, C: 1, L: De, R: Te, D: je, V: C, I: q, H: B, N: Ue, U: ke, B: Le, F: Me }, _e = W.litHtmlPolyfillSupport;
var ge;
_e == null || _e(Z, q), ((ge = W.litHtmlVersions) != null ? ge : W.litHtmlVersions = []).push("3.2.1");
const nt = (t, e, n) => {
  var r, s;
  const o = (r = n == null ? void 0 : n.renderBefore) != null ? r : e;
  let i = o._$litPart$;
  if (i === void 0) {
    const a = (s = n == null ? void 0 : n.renderBefore) != null ? s : null;
    o._$litPart$ = i = new q(e.insertBefore(I(), a), a, void 0, n != null ? n : {});
  }
  return i._$AI(t), i;
};
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
let O = class extends j {
  constructor() {
    super(...arguments), this.renderOptions = { host: this }, this._$Do = void 0;
  }
  createRenderRoot() {
    var e;
    const n = super.createRenderRoot();
    return (e = this.renderOptions).renderBefore != null || (e.renderBefore = n.firstChild), n;
  }
  update(e) {
    const n = this.render();
    this.hasUpdated || (this.renderOptions.isConnected = this.isConnected), super.update(e), this._$Do = nt(n, this.renderRoot, this.renderOptions);
  }
  connectedCallback() {
    var e;
    super.connectedCallback(), (e = this._$Do) == null || e.setConnected(!0);
  }
  disconnectedCallback() {
    var e;
    super.disconnectedCallback(), (e = this._$Do) == null || e.setConnected(!1);
  }
  render() {
    return N;
  }
};
var $e;
O._$litElement$ = !0, O.finalized = !0, ($e = globalThis.litElementHydrateSupport) == null || $e.call(globalThis, { LitElement: O });
const ye = globalThis.litElementPolyfillSupport;
ye == null || ye({ LitElement: O });
var ve;
((ve = globalThis.litElementVersions) != null ? ve : globalThis.litElementVersions = []).push("4.1.1");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const rt = { attribute: !0, type: String, converter: K, reflect: !1, hasChanged: Q }, st = (t = rt, e, n) => {
  const { kind: r, metadata: s } = n;
  let o = globalThis.litPropertyMetadata.get(s);
  if (o === void 0 && globalThis.litPropertyMetadata.set(s, o = /* @__PURE__ */ new Map()), o.set(n.name, t), r === "accessor") {
    const { name: i } = n;
    return { set(a) {
      const c = e.get.call(this);
      e.set.call(this, a), this.requestUpdate(i, c, t);
    }, init(a) {
      return a !== void 0 && this.P(i, void 0, t), a;
    } };
  }
  if (r === "setter") {
    const { name: i } = n;
    return function(a) {
      const c = this[i];
      e.call(this, a), this.requestUpdate(i, c, t);
    };
  }
  throw Error("Unsupported decorator location: " + r);
};
function v(t) {
  return (e, n) => typeof n == "object" ? st(t, e, n) : ((r, s, o) => {
    const i = s.hasOwnProperty(o);
    return s.constructor.createProperty(o, i ? { ...r, wrapped: !0 } : r), i ? Object.getOwnPropertyDescriptor(s, o) : void 0;
  })(t, e, n);
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
function it(t) {
  return v({ ...t, state: !0, attribute: !1 });
}
/**
 * @license
 * Copyright 2020 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Ie = Symbol.for(""), ot = (t) => {
  if ((t == null ? void 0 : t.r) === Ie) return t == null ? void 0 : t._$litStatic$;
}, at = (t) => ({ _$litStatic$: t, r: Ie }), Ee = /* @__PURE__ */ new Map(), ct = (t) => (e, ...n) => {
  const r = n.length;
  let s, o;
  const i = [], a = [];
  let c, l = 0, d = !1;
  for (; l < r; ) {
    for (c = e[l]; l < r && (o = n[l], (s = ot(o)) !== void 0); ) c += s + e[++l], d = !0;
    l !== r && a.push(o), i.push(c), l++;
  }
  if (l === r && i.push(e[r]), d) {
    const p = i.join("$$lit$$");
    (e = Ee.get(p)) === void 0 && (i.raw = i, Ee.set(p, e = i)), n = a;
  }
  return t(e, ...n);
}, kt = ct(tt);
var z = /* @__PURE__ */ ((t) => (t.NO_SYNC = "no-sync", t))(z || {}), P = /* @__PURE__ */ ((t) => (t.VISIBLE = "visible", t.IDLE = "idle", t.INTERACTION = "interaction", t.MEDIA = "media", t.DEFINED = "defined", t))(P || {}), g = /* @__PURE__ */ ((t) => (t.BLUR = "beacon-event-blur", t.CHANGE = "beacon-event-change", t.CLICK = "beacon-event-click", t.FOCUS = "beacon-event-focus", t.HIDE = "beacon-event-hide", t.INPUT = "beacon-event-input", t.KEYDOWN = "beacon-event-keydown", t.MOUSEDOWN = "beacon-event-mousedown", t.MOUSEENTER = "beacon-event-mouseenter", t.MOUSELEAVE = "beacon-event-mouseleave", t.RENDER = "beacon-event-render", t.SORT = "beacon-event-sort", t.DEFERRED_CHILD = "beacon-event-deferred-child", t.RENDER_ACTION_ZONE = "beacon-event-render-action-zone", t.RENDER_TABLE_CARDS = "beacon-event-render-table-cards", t.LANGUAGE_SELECT = "beacon-event-language-select", t.CLOSE = "beacon-event-close", t.OPEN = "beacon-event-open", t.NAV_LANGUAGE_SELECT = "beacon-event-language-selected", t.CLOSE_ACTIVE_NAV_ITEM = "beacon-event-close-active-nav-item", t.ELEMENT_READY = "beacon-event-element-ready", t.ACTIVE_NAV_ITEM = "beacon-event-active-nav-item-change", t.DRILLDOWN = "beacon-event-drilldown", t.DRILLUP = "beacon-event-drillup", t.HEADER_ZONE_CLICK = "beacon-event-navigation-header-zone-click", t.COUNTRY_SELECTOR_CLOSE = "beacon-event-country-selector-close", t.COUNTRY_SELECTOR_OPEN = "beacon-event-country-selector-open", t.COUNTRY_FLAG_CHANGE = "beacon-event-country-flag-change", t.HAS_CURRENT_LINK = "beacon-event-has-current-link", t.REGISTER = "beacon-event-register", t.SKIP_LINK_CLICK = "beacon-event-skip-link-click", t.METRICS_SECTION_CHANGE = "beacon-event-metrics-section-change", t.METRICS_LOCATION_CHANGE = "beacon-event-metrics-location-change", t.METRICS_ID_CHANGE = "beacon-event-metrics-id-change", t.NAVIGATION_LINK_CLICK = "beacon-event-navigation-link-click", t.DISMISS = "beacon-event-dismiss", t.TOGGLE = "beacon-event-toggle", t.CONTROL_SET = "beacon-event-control-set", t.PAGINATE = "beacon-event-paginate", t.VIDEO_BRIGHTCOVE_READY = "beacon-event-video-brightcove-ready", t))(g || {}), lt = /* @__PURE__ */ ((t) => (t.CLOSE = "close", t.KEYDOWN = "keydown", t.SCROLL = "scroll", t.CLICK = "click", t.FOCUSOUT = "focusout", t))(lt || {});
const dt = T`[beacon-tag-name]:not([focus-override]) *:focus-visible:not(.beacon-control__field,.skip-focus-override,a:not([focus-override])){box-shadow:0 0 0 var(--beacon-system-width-border-jumbo) var(--beacon-system-color-border-focus-middle),0 0 0 var(--beacon-system-width-border-maxi) var(--beacon-system-color-border-focus-outer);outline:var(--beacon-system-width-border-normal) solid var(--beacon-system-color-border-focus-inner);outline-offset:0;position:relative;z-index:1}[beacon-tag-name]:not([focus-override]) a:not([focus-override]):focus-visible{background-color:var(--beacon-system-color-background-link-focus)!important;border-block-end-color:unset;box-shadow:0 var(--beacon-system-width-border-jumbo) 0 0 var(--beacon-system-color-border-link-focus);color:var(--beacon-system-color-text-set-navy)!important;outline:none;text-decoration:none}@media (forced-colors: active){[beacon-tag-name]:not([focus-override]) a:not([focus-override]):focus-visible{border-block-end:var(--beacon-system-width-border-jumbo) solid var(--beacon-system-color-border-link-focus)}}`, pt = T`:host(:not([focus-override],.skip-focus-override)) *:not(.skip-focus-override,a:not([focus-override])):focus-visible{box-shadow:0 0 0 var(--beacon-system-width-border-jumbo) var(--beacon-system-color-border-focus-middle),0 0 0 var(--beacon-system-width-border-maxi) var(--beacon-system-color-border-focus-outer);outline:var(--beacon-system-width-border-normal) solid var(--beacon-system-color-border-focus-inner);outline-offset:0;position:relative;z-index:1}:host(:not([focus-override],.skip-focus-override)) a:not([focus-override]):focus-visible{background-color:var(--beacon-system-color-background-link-focus)!important;border-block-end-color:unset;box-shadow:0 var(--beacon-system-width-border-jumbo) 0 0 var(--beacon-system-color-border-link-focus);color:var(--beacon-system-color-text-set-navy)!important;outline:none;text-decoration:none}@media (forced-colors: active){:host(:not([focus-override],.skip-focus-override)) a:not([focus-override]):focus-visible{border-block-end:var(--beacon-system-width-border-jumbo) solid var(--beacon-system-color-border-link-focus)}}`, ut = T`:root{--motion-translate-intro-macro: var(--beacon-system-breakpoint-small);--motion-translate-intro-micro: 3rem}@media (width >= 640px){:root{--motion-translate-intro-macro: var(--beacon-system-breakpoint-medium);--motion-translate-intro-micro: 4rem}}@media (width >= 960px){:root{--motion-translate-intro-macro: var(--beacon-system-breakpoint-large);--motion-translate-intro-micro: 5rem}}@media (width >= 1280px){:root{--motion-translate-intro-macro: var(--beacon-system-breakpoint-x-large);--motion-translate-intro-micro: 6rem}}@media (width >= 1600px){:root{--motion-translate-intro-macro: var(--beacon-system-breakpoint-xx-large);--motion-translate-intro-micro: 8rem}}@media (width >= 1920px){:root{--motion-translate-intro-macro: var(--beacon-system-breakpoint-xxx-large);--motion-translate-intro-micro: 9rem}}`, ht = T`[beacon-tag-name]:where([reset-spacing]) *{margin:0;padding:0}`, mt = T`:host{border:initial;box-sizing:border-box;display:block;font-size:initial;font-style:initial;font-variant:initial;font-weight:initial;line-height:initial;white-space:initial;word-break:break-word;word-spacing:initial;text-align:initial;text-indent:initial;text-transform:initial}:host *,:host *:before,:host *:after{box-sizing:inherit}input,button,textarea,select{font:inherit;letter-spacing:inherit}button{color:inherit;cursor:pointer}ul,ol{list-style-position:initial;list-style-type:revert}::slotted(:is(ul,ol)[role=list]){list-style:none;padding:0}table{caption-side:initial;empty-cells:initial;border-collapse:initial;border-spacing:initial}`, ft = T`.has-cell-padding,.has-component-padding{padding-block:var(--beacon-reference-space-3);padding-inline:var(--beacon-reference-space-3)}:host{color:var(--beacon-system-color-text-standard);font-family:var(--beacon-system-font-family-body);-webkit-font-smoothing:antialiased;font-weight:var(--beacon-system-font-weight-light)}a,::slotted(a){color:var(--beacon-system-color-text-link)}a:not(:is(:hover,:focus)){text-decoration:none}:host(:not([motion-state=enabled])),:host(:not([motion-state=enabled])) :is(*,*:before,*:after),:host(:not([motion-state=enabled])) ::slotted(*:not([motion-state=enabled])){animation:none;animation-duration:.01ms!important;animation-iteration-count:1!important;scroll-behavior:auto!important;transition:none!important}@media (prefers-reduced-motion: reduce){:host([motion-state=enabled]),:host([motion-state=enabled]) :is(*,*:before,*:after),:host([motion-state]),:host([motion-state]) :is(*,*:before,*:after){animation:none;animation-duration:.01ms!important;animation-iteration-count:1!important;scroll-behavior:auto!important;transition:none!important}}`;
var k = /* @__PURE__ */ ((t) => (t.CLICKABLE = "data-metrics-clickable", t.TYPE = "data-metrics-type", t.TEXT = "data-metrics-link-text", t.LOCATION = "data-metrics-location", t))(k || {});
const A = class {
};
A.setPropertyOnEvent = (t, e, n, r) => {
  try {
    Object.defineProperty(t, n, {
      value: r || e[`${n}`],
      writable: !1
    });
  } catch (s) {
    console.warn(s);
  }
}, A.getAnalyticsEvents = () => {
  const t = ["gesture", "click", "touch", "change"];
  return Object.keys(window).filter((e) => /^on/.test(e) && new RegExp(t.join("|")).test(e) && e !== "onscrollsnapchange");
}, A.setHostEventHandlerForAnalytics = (t, e) => {
  var n;
  const r = A.getAnalyticsEvents(), s = "beaconVersion";
  for (const o of r) {
    const i = o.slice(2);
    (n = t.shadowRoot) == null || n.addEventListener(
      i,
      (a) => {
        var c;
        const l = new a.constructor(
          i,
          a
        );
        A.setPropertyOnEvent(
          l,
          null,
          s,
          e
        );
        for (const d in a)
          d !== "isTrusted" && A.setPropertyOnEvent(l, a, d);
        (c = t.shadowRoot) != null && c.contains(a.target) && t.dispatchEvent(l);
      },
      !0
    );
  }
};
let bt = A;
function It(t, e) {
  !t || !e || t instanceof SVGElement && (t.setAttribute(k.CLICKABLE, "true"), t.setAttribute(k.TYPE, "BTN"), t.getAttribute(k.TEXT) || t.setAttribute(k.TEXT, e));
}
function Ht(t) {
  return !!t.updateAnalytics;
}
function L(t, e, n, r) {
  const s = new CustomEvent(e, {
    bubbles: !0,
    cancelable: !1,
    composed: !0,
    detail: {},
    ...n
  });
  return t.dispatchEvent(s), r && r(), s;
}
const xe = (t) => !!t.deferrable, _t = (t) => {
  var e, n;
  return (!!((e = t.deferList) != null && e.includes(P.VISIBLE)) || !!((n = t.getAttribute("defer-until")) != null && n.includes(P.VISIBLE))) && t.isDeferUntilParent;
}, Ae = (t) => !!navigator.vendor && navigator.vendor.indexOf(t) > -1;
function gt(t) {
  return `${t}`.toLowerCase().replace(new RegExp(/[-_]+/, "g"), " ").replace(new RegExp(/[^\w\s]/, "g"), "").replace(
    new RegExp(/\s+(.)(\w*)/, "g"),
    (e, n, r) => `${n.toUpperCase() + r}`
  ).replace(new RegExp(/\w/), (e) => e.toUpperCase());
}
const $t = "4.2.2", yt = {
  rootMargin: "1500px",
  threshold: 0
}, _ = {
  version: $t,
  hidden: "hidden",
  safari: "Apple",
  divTagName: "div",
  deprecatedPropertyKeys: ["alternateAttributes", "sync", "alternateSlot"],
  defaultDeferOptions: yt
}, Y = (t) => {
  for (const e of t)
    if (e !== void 0) return e;
};
var He = (t) => {
  throw TypeError(t);
}, Be = (t, e, n) => e.has(t) || He("Cannot " + n), vt = (t, e, n) => (Be(t, e, "read from private field"), e.get(t)), Et = (t, e, n) => e.has(t) ? He("Cannot add the same private member more than once") : e instanceof WeakSet ? e.add(t) : e.set(t, n), we = (t, e, n, r) => (Be(t, e, "write to private field"), e.set(t, n), n), xt = Object.defineProperty, E = (t, e, n, r) => {
  for (var s = void 0, o = t.length - 1, i; o >= 0; o--)
    (i = t[o]) && (s = i(e, n, s) || s);
  return s && xt(e, n, s), s;
}, U, b;
const y = (b = class extends O {
  constructor() {
    super(...arguments), Et(this, U), we(this, U, !1), this.isDeferUntilParent = !1, this.deferrable = !0, this.focusOverride = !1, this.resetSpacing = !1, this.beaconTagName = "", this.version = _.version, this.motionState = "disabled", this.prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches, this.alternateSlots = /* @__PURE__ */ new Map(), this.onslotchange = (t) => {
      const { target: e } = t, n = e.name;
      this.slotDeprecationWarn(n);
    }, this.resolvedDeferOptions = {
      root: document,
      rootMargin: _.defaultDeferOptions.rootMargin,
      threshold: _.defaultDeferOptions.threshold
    }, this.deferUntilVisible = () => new Promise((t) => {
      if (!("IntersectionObserver" in window))
        t();
      else {
        const e = new IntersectionObserver((n) => {
          const [r] = n;
          r.isIntersecting && (e.disconnect(), t());
        }, this.getResolvedDeferOptions() || null);
        e.observe(this);
      }
    }), this.deferUntilIdle = () => {
      const t = new Promise((e) => {
        document.readyState !== "complete" ? window.addEventListener("load", () => e(), { once: !0 }) : e();
      });
      return "requestIdleCallback" in window ? Promise.all([
        new Promise((e) => {
          requestIdleCallback(() => {
            e();
          });
        }),
        t
      ]) : t;
    }, this.deferredChildEventHandler = (t) => new Promise((e) => {
      var n;
      t instanceof CustomEvent && (n = t.detail) != null && n.type ? (this.deferOptions && this.setDeferOptions(this.deferOptions), this.selectDeferUntilHandler(t.detail.type).then(() => e())) : e();
    }), this.renderStyles = (t, e) => {
      const n = [], r = new Set(
        this.constructor[t].flat(1 / 0)
      );
      for (const s of r)
        n.push(s);
      n.reverse().forEach((s, o) => {
        var i;
        const a = document.createElement("style");
        a.setAttribute("slot", "lightDOM");
        let c = !1;
        if (a.textContent = s.toString(), e) {
          const l = Math.abs(o - n.length), d = `${e}-${l}`, p = this.getClosestHost();
          a.id = d, (p && (i = p == null ? void 0 : p.shadowRoot) != null && i.querySelector(`#${d}`) || !p && document.querySelector(`#${d}`)) && (c = !0);
        }
        c || this.prepend(a);
      });
    }, this.renderComponentModeStyles = (t = `${this.beaconTagName}-mode-styles`) => {
      var e;
      (e = this.componentModeStyles) == null || e.forEach((n, r) => {
        const s = document.createElement("style");
        s.textContent = n.toString();
        const o = `${t}-${r}`;
        s.id = o, !document.querySelector(`#${o}`) && document.head.append(s);
      });
    }, this.getScopedTag = (t) => {
      if (!t) return null;
      const e = this.getScopedTagPrefix() ? `${this.getScopedTagPrefix()}-${t}` : t;
      return at(e);
    };
  }
  /**
   * Extends Lit's definition of `observedAttributes`.
   *
   * Returns a list of attributes corresponding to the registered properties.
   * @nocollapse
   * @category attributes
   */
  static get observedAttributes() {
    const t = [];
    return this.hasDeprecatedProperties && this.alternateAttributesMap.forEach((e, n) => {
      this.elementProperties.has(e) && t.push(n);
    }), t.push(...super.observedAttributes), t;
  }
  /**
   * Extends Lit's default attributeChangedCallback to support deprecated properties.
   *
   * @param name Attribute name
   * @param _old Old value
   * @param value New value
   */
  attributeChangedCallback(t, e, n) {
    const {
      alternateAttributesMap: r,
      hasDeprecatedProperties: s,
      elementProperties: o
    } = this.constructor;
    if (s && r.size > 0) {
      const i = r.get(t), a = this.getPropertyOptions(t).alternateSlot;
      if (i) {
        const c = o.get(i);
        if (i !== z.NO_SYNC && (n === null ? this.removeAttribute(
          (c == null ? void 0 : c.attribute) || i
        ) : this.setAttribute(
          (c == null ? void 0 : c.attribute) || i,
          n || ""
        )), a) {
          const l = this.createPropertyDeprecationForSlotMessage(t, a);
          console.warn(l);
        } else
          console.warn(
            this.createPropertyDeprecationMessage(
              t,
              i,
              c == null ? void 0 : c.attribute
            )
          );
      }
    }
    super.attributeChangedCallback(t, e, n);
  }
  /**
   * Generates a console message to log when deprecated properties were used.
   *
   * @param name attribute that triggered the message
   * @param prop property that the attribute aligns to
   * @param attribute correct attribute to use
   */
  createPropertyDeprecationMessage(t, e, n) {
    let r = `Beacon Warning: [${t}] will be deprecated as a prop of <${this.beaconTagName.toLowerCase()}> in a future release.`;
    return e !== z.NO_SYNC && (r += ` Start using [${n || e}] instead to avoid future disruptions.`), r;
  }
  /**
   * Generates a console message to log when a deprecated property is used
   * and should instead use the designated slot
   * @param property the name of the property being deprecated
   * @param slot the designated slot to use instead
   */
  createPropertyDeprecationForSlotMessage(t, e) {
    return `Beacon Warning: '${t}' will be deprecated as a prop of <${this.beaconTagName.toLowerCase()}> in a future release. Start using the slot: '${e}' instead to avoid future disruptions`;
  }
  /**
   * Extend's Lit's `createProperty` method for our own uses.
   * @param name Property Name
   * @param options Options for the property
   */
  static createProperty(t, e) {
    if (e) {
      const { alternateAttributes: n, attribute: r } = e, s = Object.prototype.hasOwnProperty.call(
        e,
        "sync"
      );
      if (_.deprecatedPropertyKeys.forEach((o) => {
        Object.prototype.hasOwnProperty.call(e, o) && (this.hasDeprecatedProperties = !0);
      }), n && n.forEach((o) => {
        this.alternateAttributesMap.set(o, t);
      }), s && !e.sync) {
        const o = r != null ? r : t;
        this.alternateAttributesMap.set(
          o,
          z.NO_SYNC
        );
      }
    }
    super.createProperty(t, e);
  }
  /**
   * This is used to return the property options of a property when given only its attribute name,
   * e.g. the property pageTitle with an attribute name of 'page-title' will have no way of accessing its options
   * directly when attempting to grab the options with 'page-title' and elementProperties.get().
   * @param name the resolved attribute name of a `@property()` decorated property
   * @returns the options passed in `@property()` e.g. `{attribute: 'attribute-name', reflect: true}`
   */
  getPropertyOptions(t) {
    var e;
    const { elementProperties: n } = this.constructor;
    return (e = n.get(t)) != null ? e : [...n.values()].find(
      (r) => r.attribute === t
    );
  }
  /**
   * Getter that returns private resolvedDeferOptions
   * @returns resolvedDeferOptions that are passed to an IntersectionObserver
   */
  getResolvedDeferOptions() {
    return this.resolvedDeferOptions;
  }
  /** callback method for the beacon-on-click event*/
  onBeaconClick() {
  }
  /** callback method for the beacon-on-keydown event*/
  onBeaconKeydown() {
  }
  /** callback method for the beacon-on-toggle event*/
  onBeaconToggle() {
  }
  /**
   * Converts space separated list prop of `defer-until` to a DeferUntil enum array
   */
  get deferList() {
    var t, e;
    return (e = (t = this.deferUntil) == null ? void 0 : t.split(" ").map((n) => n).filter((n) => !!n)) != null ? e : [];
  }
  /**
   * Logs a warning based on the slot name provided and details from the alternate slots map
   *
   * @param slotName name of the slot that has changed
   */
  slotDeprecationWarn(t) {
    const e = this.alternateSlots.get(t);
    if (e !== void 0) {
      let n;
      const r = `The '${t}' for for '${this.beaconTagName}'`;
      Object.keys(e).some((s) => s.match("alternate")) ? e.alternateAttribute ? n = `will be replaced by the element property '${e.alternateAttribute}'` : n = `will be replaced by the '${e.alternateSlots}' slot` : n = "will be removed", console.warn(
        `Beacon Deprecation Warning: ${r} ${n} in foundation@${e.version}`
      );
    }
  }
  /**
   * A setter that takes user input deferOptions and resolves them compared to existing deferOptions. Enables the setting
   * of isolated deferOptions and leaves unchanged options previously set intact.
   * @param options the defer-options object used in IntersectionObserver.
   */
  setDeferOptions(t) {
    if (!t) return;
    const e = { ...this.resolvedDeferOptions }, n = Y([
      t == null ? void 0 : t.root,
      e == null ? void 0 : e.root
    ]), r = Y([
      t == null ? void 0 : t.rootMargin,
      e == null ? void 0 : e.rootMargin
    ]), s = Y([
      t == null ? void 0 : t.threshold,
      e == null ? void 0 : e.threshold
    ]);
    this.resolvedDeferOptions = {
      root: n instanceof Document || n instanceof Element ? n : document,
      rootMargin: typeof r == "string" ? r : _.defaultDeferOptions.rootMargin,
      threshold: Array.isArray(s) || typeof s == "number" ? s : _.defaultDeferOptions.threshold
    };
  }
  /**
   * Hides the visibility of the component. Used when component is deferred until specified condition.
   * @returns void
   */
  setDeferVisibilityStyles() {
    Ae(_.safari) || (this.style.visibility = _.hidden);
  }
  /**
   * Sets the component styles to visibile. Used when component meets condition specified to defer until.
   * @returns void
   */
  removeDeferVisibilityStyles() {
    Ae(_.safari) || this.style.removeProperty("visibility");
  }
  /**
   * Searches for a parent of component with defer-until attribute that is not solely defer-until="defined".
   * @returns closest lightDOM parent with [defer-until] attribute or null
   */
  getClosestDeferredParent() {
    const t = '[defer-until]:not([defer-until="defined"])', e = this.closest(
      t
    );
    if (e && xe(e))
      return e;
    const n = this.getClosestHost();
    if (n) {
      const r = n.closest(
        t
      );
      if (r && xe(r))
        return r;
    }
    return null;
  }
  /**
   * Runs in shadowDOM context. This method searches for the ShadowRoot then returns host of ShadowRoot.
   * @returns host element of this component of the shadowDOM
   */
  getClosestHost() {
    let t = this && this.parentNode;
    for (; t; ) {
      if (t instanceof ShadowRoot)
        return t.host;
      t = t.parentNode;
    }
    return null;
  }
  /**
   * Selects which deferUntil handler method to run on component.
   * @param deferUntilCriteria DeferUntil option that determines how component should defer.
   */
  selectDeferUntilHandler(t) {
    return new Promise((e) => {
      t.includes(P.VISIBLE) ? (this.setDeferVisibilityStyles(), this.deferUntilVisible().then(() => {
        this.removeDeferVisibilityStyles(), e();
      })) : t.includes(P.IDLE) ? (this.setDeferVisibilityStyles(), this.deferUntilIdle().then(() => {
        this.removeDeferVisibilityStyles(), e();
      })) : e();
    });
  }
  /**
   * Method that handles setup of deferUntil handler when deferUntil is configured locally via deferUntil prop on the component.
   */
  handleLocalDeferUntil() {
    return new Promise((t) => {
      this.deferOptions && this.setDeferOptions(this.deferOptions), this.selectDeferUntilHandler(this.deferList).then(() => t());
    });
  }
  /**
   * Method that looks for the closest element to the component with 'defer-until' attribute and either
   * applies the parent's deferUntil logic to the component immediately, if the parent is already registered as `isDeferUntilParent`,
   * or adds an eventListener to toggle parent's deferUntil logic on component once parent has emitted the beacon-event-deferred-child Event.
   * @param closestDeferredParent is the closest element to the component that has the 'defer-until' attribute set.
   */
  handleParentalDeferUntil(t) {
    return new Promise((e) => {
      if (t === null)
        e();
      else if (_t(t))
        this.selectDeferUntilHandler(t.deferList).then(
          () => e()
        );
      else {
        const n = (r) => {
          this.deferredChildEventHandler(r).then(() => {
            t.removeEventListener(
              g.DEFERRED_CHILD,
              n
            ), e();
          });
        };
        t.addEventListener(
          g.DEFERRED_CHILD,
          n
        );
      }
    });
  }
  /**
   * This method handles defer logic. If component has deferUntil prop set, it calls method to handle deferUntil logic locally.
   * Else if component has a closest element with 'defer-until' attribute set, it handles deferUntil based on closestDeferredParent's deferUntil settings.
   * If neither of the above criteria match, it completes connectedCallback.
   */
  handleDefer() {
    return new Promise((t) => {
      if (!this.deferrable || this.motionState === "enabled")
        t();
      else if (this.deferUntil)
        this.registerAsDeferUntilParent(), this.handleLocalDeferUntil().then(() => t());
      else {
        const e = this.getClosestDeferredParent();
        e ? this.handleParentalDeferUntil(e).then(
          () => t()
        ) : t();
      }
    });
  }
  /**
   * Method that registers a component as parental component that can trigger deferUntil logic in child components. It should be placed in connectedCallback
   * of all components that need to trigger deferUntil logic in child components.
   */
  registerAsDeferUntilParent() {
    if (this.deferOptions && this.setDeferOptions(this.deferOptions), this.deferList.includes(P.VISIBLE)) {
      const t = new IntersectionObserver((e) => {
        for (const n of e)
          n.target && (this.isDeferUntilParent = !0, this.emitDeferredChild(), t.disconnect());
      }, this.getResolvedDeferOptions() || null);
      t.observe(this);
    }
  }
  /**
   * @returns the scope prefix from the beginning of the tagName.
   */
  getScopedTagPrefix() {
    const t = this.tagName.toLowerCase().split("-beacon")[0];
    return t.startsWith("beacon") ? "" : t;
  }
  /**
   * Defines this web component in the CustomElementRegistry.
   * @param tagNamePrefix lowercase string that prefixes default component tag names for scoping purposes
   */
  static registerComponent(t) {
    if (this.tagName) {
      const e = `${t ? `${t}-` : ""}${this.tagName}`;
      if (!customElements.get(e)) {
        const n = class extends this {
        };
        Object.defineProperty(n, "name", {
          value: gt(e)
        }), customElements.define(e, n), customElements.whenDefined(e).then(() => {
          const r = new CustomEvent(g.REGISTER, {
            detail: e
          });
          document.dispatchEvent(r);
        }).catch((r) => {
          console.error(
            `Error defining Beacon Component ${e} in CustomElementsRegistry: `,
            r
          );
        });
      }
    }
  }
  static renderGlobalRootStyles() {
    this.globalRootStyles.forEach((t, e) => {
      if (document.getElementById(`beacon-global-root-styles-${e}`)) return;
      const n = document.createElement("template");
      n.innerHTML = `
        <style id="beacon-global-root-styles-${e}">
          ${t.toString()}
        </style>
      `;
      const r = n.content.cloneNode(!0);
      document.head.append(r);
    });
  }
  /**
   * This will return beacon tag name to scoped tag Name
   * @param tag beaconTagName
   * @returns Scoped tagName
   */
  getScopedTagName(t) {
    return t ? this.getScopedTagPrefix() ? `${this.getScopedTagPrefix()}-${t}` : t : "";
  }
  /**
   * @param event
   * @event {BeaconEvents} beacon-event-click - emits when clicked
   */
  emitClick() {
    L(this, g.CLICK, void 0, this.onBeaconClick);
  }
  /**
   * @param event
   * @event {BeaconEvents} beacon-event-keydown - emits when a keydown event would be fired
   */
  emitKeydown() {
    L(this, g.KEYDOWN, void 0, this.onBeaconKeydown);
  }
  emitToggle() {
    L(this, g.TOGGLE, void 0, this.onBeaconToggle);
  }
  /**
   * Emitter of beacon-event-deferred-child event used by components registered as deferUntilParents.
   * @param event
   * @event {BeaconEvents} beacon-event-deferred-child - signals to children of a deferUntil parent that they should behave as deferUntil components
   */
  emitDeferredChild() {
    L(this, g.DEFERRED_CHILD, {
      detail: {
        element: this,
        type: this.deferList
      }
    });
  }
  /**
   * Emits a beacon-event-keydown event as a default to mousedown handling
   * @param event
   */
  handleKeydown(t) {
    this.emitKeydown();
  }
  /**
   * Check if the component has a specific mixin applied.
   * @param name Name of the mixin to look for.
   * @returns True if the class contains that mixin.
   */
  hasMixin(t) {
    var e;
    try {
      if (!((e = this.constructor().mixins) != null && e.includes(t))) throw new Error(`This class does not implement ${t}.`);
      return !0;
    } catch (n) {
      return console.error(n), !1;
    }
  }
  /**
   * Check for a field with a specific value on the class.
   *
   * Useful when a mixin relies on the existance of another mixin.
   *
   * @param field Class property to check for on the mixin.
   * @param value Value of the given class field to check for.
   * @returns True if the value of the field is the same.
   */
  hasFieldAndValue(t, e) {
    try {
      if (!(t in this))
        throw new Error(`${t} does not exist on this: ${this}`);
      return this[t] === e;
    } catch (n) {
      return console.error(n), !1;
    }
  }
  /**
   * Placeholder method that collects all method functionality that is decorated with @prerender
   * and is called in connectedCallback. This method should not be overridden directly.
   * The goal is to prevent direct overrides of connectedCallback in subclasses
   */
  prerender(...t) {
  }
  /**
   * Placeholder method that collects all method functionality that is decorated with @postrender
   * and is called in firstUpdated. This method should not be overridden directly.
   * The goal is to prevent direct overrides of firstUpdated in subclasses
   */
  postrender(...t) {
  }
  /**
   * Placeholder method that collects all method functionality that is decorated with @destroy
   * and is called in disconnectedCallback. This method should not be overridden directly.
   * The goal is to prevent direct overrides of disconnectedCallback in subclasses
   */
  destroy(...t) {
  }
  /**
   * Enables or disables motion on this component.
   *
   * Looks for the closest element with `[motion-state]` to find its value and set the motion state of the component equal to that.
   *
   * @link https://developer.mozilla.org/en-US/docs/Web/API/Element/closest Element.closest() will return the element itself if it matches the selector.
   */
  setMotionState() {
    if (this.motionState === "enabled")
      return;
    const t = this.closest("[motion-state]"), e = t == null ? void 0 : t.getAttribute("motion-state");
    e && (e === "enabled" || e === "disabled") && (this.motionState = e);
  }
  /**
   * Removes the "defined" string value from defer-until attribute while leaving in place any other subString in the attribute.
   * This is a clean-up function called in connectedCallback that enables usage of applitools UltraFastGrid with defer-until="defined"
   * components.
   */
  removeDeferUntilDefined() {
    const t = this.getAttribute("defer-until");
    if (t) {
      const e = t.split(" ").filter((r) => r), n = e.length;
      if (n === 1 && e.includes("defined") || n === 0)
        this.removeAttribute("defer-until");
      else {
        const r = e.filter((s) => s !== "defined").join(" ");
        this.setAttribute("defer-until", r);
      }
    } else this.hasAttribute("defer-until") && !t && this.removeAttribute("defer-until");
  }
  renderLightDOMStyles() {
    this.renderStyles(
      "lightDOMStyles",
      `${this.beaconTagName}-light-DOM-styles`
    ), this.renderStyles("globalLightDOMStyles", "beacon-global-light-DOM-styles");
  }
  connectedCallback() {
    this.setMotionState(), this.removeDeferUntilDefined(), this.handleDefer().then(() => {
      super.connectedCallback(), vt(this, U) || (bt.setHostEventHandlerForAnalytics(this, this.version), we(this, U, !0)), b.renderGlobalRootStyles(), this.renderLightDOMStyles(), this.prerender();
    });
  }
  firstUpdated() {
    this.postrender(), L(this, g.ELEMENT_READY);
  }
  disconnectedCallback() {
    super.disconnectedCallback(), this.destroy();
  }
}, U = /* @__PURE__ */ new WeakMap(), b.styles = [mt, ft, pt], b.tagName = "", b.shadowRootOptions = {
  ...O.shadowRootOptions,
  delegatesFocus: !0
}, b.alternateAttributesMap = /* @__PURE__ */ new Map(), b.hasDeprecatedProperties = !1, b.mixins = [], b.lightDOMStyles = [], b.globalLightDOMStyles = [
  ht,
  dt
], b.globalRootStyles = [ut], b);
E([
  v({ attribute: "focus-override", type: Boolean, reflect: !0 })
], y.prototype, "focusOverride");
E([
  v({ attribute: "reset-spacing", type: Boolean, reflect: !0 })
], y.prototype, "resetSpacing");
E([
  v({ attribute: "beacon-tag-name", type: String, reflect: !0 })
], y.prototype, "beaconTagName");
E([
  v({ attribute: "beacon-version", reflect: !0 })
], y.prototype, "version");
E([
  v({ attribute: "defer-until" })
], y.prototype, "deferUntil");
E([
  v({ attribute: "defer-options" })
], y.prototype, "deferOptions");
E([
  v({ attribute: "motion-state", reflect: !0 })
], y.prototype, "motionState");
E([
  it()
], y.prototype, "prefersReducedMotion");
let Bt = y;
const At = (t) => t.replace(
  /[A-Z]+(?![a-z])|[A-Z]/g,
  (e, n) => (n ? "-" : "") + e.toLowerCase()
), wt = (t) => /^([a-z](?![\d])|[\d](?![a-z]))+(-?([a-z](?![\d])|[\d](?![a-z])))*$|^$/.test(
  t
);
function St(t) {
  return `${t}`.toLowerCase().replace(new RegExp(/[-_]+/, "g"), " ").replace(new RegExp(/[^\w\s]/, "g"), "").replace(
    new RegExp(/\s+(.)(\w*)/, "g"),
    (e, n, r) => `${n.toUpperCase() + r}`
  ).replace(new RegExp(/\w/), (e) => e.toUpperCase());
}
typeof process == "object" && process && process.env && process.env.UNIT_TESTING_ENV;
function Ct(t) {
  switch (t) {
    case "./__temp-separated/beacon-accordion/index.js":
      return import("./index-yf8GK7Th-BYd_a0D5.js");
    case "./__temp-separated/beacon-accordion-item/index.js":
      return import("./index-BXRA-e49-EyHTVgf2.js");
    case "./__temp-separated/beacon-action-bar/index.js":
      return import("./index-BHVN6jRo-dT4FBbda.js");
    case "./__temp-separated/beacon-anchor-navigation/index.js":
      return import("./index-u68TXH75-CjkJWnPP.js");
    case "./__temp-separated/beacon-area-chart/index.js":
      return import("./index-bMohN88S-Br3PuWOs.js");
    case "./__temp-separated/beacon-article-tile/index.js":
      return import("./index-Bg8noQdm-CsxrA40H.js");
    case "./__temp-separated/beacon-async-append.d/index.js":
      return import("./index-DWDsxv8E-HvyJD61X.js");
    case "./__temp-separated/beacon-async-directive.d/index.js":
      return import("./index-CcruWZKi-ISxGjIlj.js");
    case "./__temp-separated/beacon-async-replace.d/index.js":
      return import("./index-cqwqN7RP-BNiZdhUR.js");
    case "./__temp-separated/beacon-audio/index.js":
      return import("./index-C6CrxQ3H-DfLUm50d.js");
    case "./__temp-separated/beacon-bar-chart/index.js":
      return import("./index-vPnjGkvX-75dfWuU8.js");
    case "./__temp-separated/beacon-bio-card/index.js":
      return import("./index-CiuA4Lmq-C50mk3mK.js");
    case "./__temp-separated/beacon-breadcrumbs/index.js":
      return import("./index-BNKzwlsM-DZmUhWqu.js");
    case "./__temp-separated/beacon-button/index.js":
      return import("./index-CSz7ljnF-C_owU7bA.js");
    case "./__temp-separated/beacon-byline/index.js":
      return import("./index-yb6p0Y_W-FzYpL024.js");
    case "./__temp-separated/beacon-cache.d/index.js":
      return import("./index-C49I_nL_-9gbBf6E0.js");
    case "./__temp-separated/beacon-carousel/index.js":
      return import("./index-BjjPj_bL-C0o0U0vE.js");
    case "./__temp-separated/beacon-choose.d/index.js":
      return import("./index-Bgx6SscZ-BvDxkGxn.js");
    case "./__temp-separated/beacon-class-map.d/index.js":
      return import("./index-sl1_h-2--CZKxsp0Y.js");
    case "./__temp-separated/beacon-collection-tile/index.js":
      return import("./index-BNGj4i5p-BUv0rsvN.js");
    case "./__temp-separated/beacon-contact-tile/index.js":
      return import("./index-XRo2jp98-C3jq_hax.js");
    case "./__temp-separated/beacon-control/index.js":
      return import("./index-Bw27YOvZ-BDRx1q6p.js");
    case "./__temp-separated/beacon-control-group/index.js":
      return import("./index-CAykRV0q-DjscjMWh.js");
    case "./__temp-separated/beacon-country/index.js":
      return import("./index-Bry-l637-BwMXIR1o.js");
    case "./__temp-separated/beacon-country-selector/index.js":
      return import("./index-CkZCdHj9-BRITj9PG.js");
    case "./__temp-separated/beacon-custom-element.d/index.js":
      return import("./index-3Ky_KbTn-BYAVYlym.js");
    case "./__temp-separated/beacon-data-list/index.js":
      return import("./index-DvYSnaOZ-j4m5kakQ.js").then((e) => e.index);
    case "./__temp-separated/beacon-decorators.d/index.js":
      return import("./index-DjxEPQib-Dsk6QPE9.js");
    case "./__temp-separated/beacon-directive-helpers.d/index.js":
      return import("./index-Cg6PELoU-ZyMPvnZn.js");
    case "./__temp-separated/beacon-directive.d/index.js":
      return import("./index-DUPC5men-DaO0oGZM.js");
    case "./__temp-separated/beacon-divider/index.js":
      return import("./index-DmZovyZY-DNQBWqk4.js");
    case "./__temp-separated/beacon-donut-chart/index.js":
      return import("./index-DMLaj7QB-DCUNGES-.js");
    case "./__temp-separated/beacon-drawer/index.js":
      return import("./index-BBWF6BEv-KNAxxx3y.js");
    case "./__temp-separated/beacon-error/index.js":
      return import("./index-CZ8sBWcs-CnXhkCVR.js");
    case "./__temp-separated/beacon-event-options.d/index.js":
      return import("./index-DPRe0aaE-Dm_fRTyp.js");
    case "./__temp-separated/beacon-filter-chip/index.js":
      return import("./index-BbmVuU25-BNf2HZ2D.js");
    case "./__temp-separated/beacon-footer/index.js":
      return import("./index-CAsRNM9L-BrDk6P1m.js");
    case "./__temp-separated/beacon-gauge-chart/index.js":
      return import("./index-J7u2cpww-BZkb92S1.js");
    case "./__temp-separated/beacon-guard.d/index.js":
      return import("./index-BIsqFChh-BnRis4Oi.js");
    case "./__temp-separated/beacon-hero/index.js":
      return import("./index-B-T3PHZP-CEr7NsHy.js");
    case "./__temp-separated/beacon-html.d/index.js":
      return import("./index-CYA3oVVY-hqmzhX60.js");
    case "./__temp-separated/beacon-icon/index.js":
      return import("./index-DQIzg92N-Dud4Boud.js");
    case "./__temp-separated/beacon-icon-button/index.js":
      return import("./index-BAXV4Tb7-BlXMxJ8u.js");
    case "./__temp-separated/beacon-id-card/index.js":
      return import("./index-DWLV56nz-wmBTUkFp.js");
    case "./__temp-separated/beacon-if-defined.d/index.js":
      return import("./index-CyV8n-JU-hc7Iq8tb.js");
    case "./__temp-separated/beacon-index.all.d/index.js":
      return import("./index-CIUpExT0-X0WBRZ8a.js");
    case "./__temp-separated/beacon-index.d/index.js":
      return import("./index-CUehEOy9-BQK1huLI.js");
    case "./__temp-separated/beacon-index.test-components/index.js":
      return import("./index-WzEPNEoJ-Bv5payS6.js");
    case "./__temp-separated/beacon-input-chip/index.js":
      return import("./index-lnkkmPJl-DzaxoiIV.js");
    case "./__temp-separated/beacon-join.d/index.js":
      return import("./index-DeJaZgXv-CZS-BhPm.js");
    case "./__temp-separated/beacon-key-facts/index.js":
      return import("./index-B2dzFvQb-yD2rV1dW.js");
    case "./__temp-separated/beacon-keyed.d/index.js":
      return import("./index-BdRjMG-L-Bo75I2rv.js");
    case "./__temp-separated/beacon-line-chart/index.js":
      return import("./index-aKS1hQy0-DqXIysMi.js");
    case "./__temp-separated/beacon-live.d/index.js":
      return import("./index-BGAZPIBx-BjLXRDPt.js");
    case "./__temp-separated/beacon-loading-spinner/index.js":
      return import("./index-CbI2hCcO-SU7NeVeC.js");
    case "./__temp-separated/beacon-map.d/index.js":
      return import("./index-CSPViVrn-qlGozkNP.js");
    case "./__temp-separated/beacon-menu/index.js":
      return import("./index-CBwxGJg8-ZTXCrC9p.js");
    case "./__temp-separated/beacon-meter-chart/index.js":
      return import("./index-4awEUpGQ-CARNhiRx.js");
    case "./__temp-separated/beacon-modal/index.js":
      return import("./index-CinX9Bqd-0VoZ760p.js");
    case "./__temp-separated/beacon-multi-select/index.js":
      return import("./index-soSdqt1k-Dx-aRjbl.js");
    case "./__temp-separated/beacon-multi-select-optgroup/index.js":
      return import("./index-DfJHULfM-CivtZk9I.js").then((e) => e.index);
    case "./__temp-separated/beacon-multi-select-option/index.js":
      return import("./index-Bo8XBgZ9-DjRA4PXP.js");
    case "./__temp-separated/beacon-navigation/index.js":
      return import("./index-CObmVDwJ-fwNpSa9s.js").then((e) => e.index);
    case "./__temp-separated/beacon-navigation-button/index.js":
      return import("./index-CObmVDwJ-fwNpSa9s.js").then((e) => e.index$1);
    case "./__temp-separated/beacon-navigation-expanded/index.js":
      return import("./index-CObmVDwJ-fwNpSa9s.js").then((e) => e.index$2);
    case "./__temp-separated/beacon-navigation-footer/index.js":
      return import("./index-fTKxaZ2P-AzFR_pxF.js");
    case "./__temp-separated/beacon-navigation-popover/index.js":
      return import("./index-CObmVDwJ-fwNpSa9s.js").then((e) => e.index$3);
    case "./__temp-separated/beacon-navigation-toolbelt/index.js":
      return import("./index-CObmVDwJ-fwNpSa9s.js").then((e) => e.index$4);
    case "./__temp-separated/beacon-notification/index.js":
      return import("./index-CAbjT1p4-BUnSCM8i.js");
    case "./__temp-separated/beacon-notification-group/index.js":
      return import("./index-D681fsMa-CMF6VmCm.js");
    case "./__temp-separated/beacon-pagination/index.js":
      return import("./index-BMf0nIcR-DEf3TaIC.js");
    case "./__temp-separated/beacon-pagination-button/index.js":
      return import("./index-DG-0buG5-DJHeyHIP.js");
    case "./__temp-separated/beacon-polyfill-support.d/index.js":
      return import("./index-CjVQj10L-CuqGAGAv.js");
    case "./__temp-separated/beacon-popover/index.js":
      return import("./index-Bg98nB2Z-AJZR1z-d.js");
    case "./__temp-separated/beacon-progress-indicator/index.js":
      return import("./index-DuzaX_tC-htF4HO7D.js");
    case "./__temp-separated/beacon-promo-tile/index.js":
      return import("./index-D7BTWqeI-D3kpYEJq.js");
    case "./__temp-separated/beacon-property.d/index.js":
      return import("./index-s3jJ0xl5-CowEOooj.js");
    case "./__temp-separated/beacon-query-all.d/index.js":
      return import("./index-BcU27z8q-BBgMqy8S.js");
    case "./__temp-separated/beacon-query-assigned-elements.d/index.js":
      return import("./index-Cy9drcVL-BliU_Vre.js");
    case "./__temp-separated/beacon-query-assigned-nodes.d/index.js":
      return import("./index-Cnb0xbru-KXklajCR.js");
    case "./__temp-separated/beacon-query-async.d/index.js":
      return import("./index-H41p79h--CNuIPjCN.js");
    case "./__temp-separated/beacon-query.d/index.js":
      return import("./index-DkrD1O-0-BhoO1gJQ.js");
    case "./__temp-separated/beacon-quote-block/index.js":
      return import("./index-puKDf555-CvP4imKP.js");
    case "./__temp-separated/beacon-range.d/index.js":
      return import("./index-hWPuSLH0-9dYjAG53.js");
    case "./__temp-separated/beacon-ref.d/index.js":
      return import("./index-BdSkduYd-DYkGeBe1.js");
    case "./__temp-separated/beacon-region-selector/index.js":
      return import("./index-Z1eTh57t-CyfwELNQ.js");
    case "./__temp-separated/beacon-repeat.d/index.js":
      return import("./index-C0jQhwVZ-CYAKcxak.js");
    case "./__temp-separated/beacon-scatter-chart/index.js":
      return import("./index-CYfRMZgd-D70N7HVb.js");
    case "./__temp-separated/beacon-segmented-control/index.js":
      return import("./index-D-FRRbN7-b5-UnIMV.js");
    case "./__temp-separated/beacon-segmented-controls/index.js":
      return import("./index-DsqS1r9C-CoDX2MVB.js");
    case "./__temp-separated/beacon-selection-tile/index.js":
      return import("./index-yqnSBPmv-BcCEQnL4.js");
    case "./__temp-separated/beacon-social-link/index.js":
      return import("./index-DDVqrDc8-CNcBl9PT.js");
    case "./__temp-separated/beacon-social-link-container/index.js":
      return import("./index-CFjOw0QQ-DVZ7ZYzG.js");
    case "./__temp-separated/beacon-state.d/index.js":
      return import("./index-uiGwYmGU-B4gTdfzL.js");
    case "./__temp-separated/beacon-static-html.d/index.js":
      return import("./index-CGDM5ynt-D47qz_-a.js");
    case "./__temp-separated/beacon-style-map.d/index.js":
      return import("./index-BKlvytSw-M6pNz2k-.js");
    case "./__temp-separated/beacon-tab/index.js":
      return import("./index-QM_6N1Iw-N6G6wmkd.js").then((e) => e.index);
    case "./__temp-separated/beacon-tab-panel/index.js":
      return import("./index-jm3vhE_Z-CNhX1xWQ.js");
    case "./__temp-separated/beacon-table/index.js":
      return import("./index-CHvrci0j-C39pJ9xg.js");
    case "./__temp-separated/beacon-table-header/index.js":
      return import("./index-Dc5IVl1D-CqOsliWT.js").then((e) => e.index);
    case "./__temp-separated/beacon-table-record/index.js":
      return import("./index-e1bYYYyL-DF8I9dNo.js").then((e) => e.index);
    case "./__temp-separated/beacon-table-stub/index.js":
      return import("./index-Dkv99wmd-BkVwFZyn.js").then((e) => e.index);
    case "./__temp-separated/beacon-tabs/index.js":
      return import("./index-Vmg6jL7a-DmG3ccjZ.js");
    case "./__temp-separated/beacon-template-content.d/index.js":
      return import("./index-ChrPO7RG-CA71tpK4.js");
    case "./__temp-separated/beacon-tooltip/index.js":
      return import("./index-CYCEOFPi-C10MTeZE.js");
    case "./__temp-separated/beacon-unsafe-html.d/index.js":
      return import("./index-K3rW__Pr-Dq70wvfy.js");
    case "./__temp-separated/beacon-unsafe-svg.d/index.js":
      return import("./index-Cdbsxh_c-omwbRAqr.js");
    case "./__temp-separated/beacon-until.d/index.js":
      return import("./index-CxHdYFJS-JjEINVML.js");
    case "./__temp-separated/beacon-video/index.js":
      return import("./index-ClwyfjgF-BVoLLiB2.js");
    case "./__temp-separated/beacon-video-playlist/index.js":
      return import("./index-elFb0sI0-B9BHSXvK.js");
    case "./__temp-separated/beacon-video-tag/index.js":
      return import("./index-CsBlkzB5-Vqz7VuE7.js");
    case "./__temp-separated/beacon-video-tile/index.js":
      return import("./index-kdHJdI1h-Bsdtq41y.js");
    case "./__temp-separated/beacon-video-transcript/index.js":
      return import("./index-DYH27qoI-C62FzTNF.js");
    case "./__temp-separated/beacon-when.d/index.js":
      return import("./index-CfbsfIe0-BDCkMiLR.js");
    default:
      return new Promise(function(e, n) {
        (typeof queueMicrotask == "function" ? queueMicrotask : setTimeout)(
          n.bind(null, new Error("Unknown variable dynamic import: " + t))
        );
      });
  }
}
const jt = (t) => !!t.registerComponent, Ot = (t) => {
  const e = At(t);
  return wt(t) || console.warn(
    `Beacon Warning: '${t}' is not a valid Beacon component tag name. Class name casing will be deprecated soon. Update to tag name "${e}".`
  ), e;
}, Pt = (t) => {
  const e = Ot(t);
  return {
    tagName: e,
    className: St(e)
  };
}, Se = async (t, e, n) => new Promise((r, s) => {
  var o;
  if (typeof e == "string") {
    const { tagName: i, className: a } = Pt(
      e
    ), c = t ? `${t}-${i}` : i;
    customElements.get(c) ? r(customElements.get(c)) : Ct(`./__temp-separated/${i}/index.js`).then((l) => {
      var d;
      const p = l[`${a}`];
      p.registerComponent(t), (d = p.nestedComponents) != null && d.length && Promise.all(
        p.nestedComponents.map(
          (f) => Se(
            t,
            f
          )
        )
      ), r(p);
    }).catch(() => {
      const l = `Beacon Registration Error: Unable to import ${e}`;
      r(l);
    });
  } else if (jt(e)) {
    const i = e;
    i.registerComponent(t), (o = i.nestedComponents) != null && o.length && Promise.all(
      i.nestedComponents.map(
        (a) => Se(
          t,
          a
        )
      )
    ), r(i);
  }
});
export {
  N as T,
  tt as Y,
  at as _,
  it as a,
  lt as b,
  g as c,
  v as h,
  T as h$1,
  It as h$2,
  nt as ie,
  Se as m,
  Bt as me,
  Lt as ne,
  Dt as o,
  k as p,
  h as u,
  L as u$1,
  kt as v,
  Q as y,
  Ht as y$1
};
